import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import CertificateGeneratorModal from './CertificateGeneratorModal';
import CertificateListPanel from './CertificateListPanel';
import { downloadCertificatesAsZipBatch } from '../utils/certificateDownloader';
import { ApiService } from '../utils/api';
import { useToast } from './Toast';
import { FileText, Download, Mail, Plus, Loader2, CheckCircle, AlertCircle, AlertTriangle } from 'lucide-react';

const CertificatesTab = ({ eventId, eventName }) => {
  const [showGeneratorModal, setShowGeneratorModal] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [showSendConfirmDialog, setShowSendConfirmDialog] = useState(false);
  const { showSuccess, showError } = useToast();

  // Download all certificates mutation
  const downloadAllCertificatesMutation = useMutation({
    mutationFn: async () => {
      // Reset progress
      setDownloadProgress({ current: 0, total: 0, percentage: 0, status: 'starting' });
      
      // Get certificate URLs from backend
      const certificates = await ApiService.downloadCertificateUrls(eventId);
      
      if (!certificates || certificates.length === 0) {
        throw new Error('No certificates found for this event');
      }
      
      // Download and create ZIP with progress tracking
      const result = await downloadCertificatesAsZipBatch(
        certificates,
        eventName || `Event_${eventId}`,
        (progress) => {
          setDownloadProgress(progress);
        },
        5 // Batch size - download 5 certificates at a time
      );
      
      return result;
    },
    onSuccess: (result) => {
      setDownloadProgress(null);
      if (result.failed > 0) {
        showSuccess(`Downloaded ${result.successful}/${result.total} certificates. ${result.failed} failed.`);
      } else {
        showSuccess(`Downloaded all ${result.successful} certificates successfully!`);
      }
    },
    onError: (error) => {
      setDownloadProgress(null);
      if (error.message.includes('No certificates found')) {
        showError('No certificates have been generated for this event yet');
      } else {
        showError('Failed to download certificates. Please try again.');
      }
      console.error('Download certificates error:', error);
    }
  });

  const handleDownloadAll = () => {
    downloadAllCertificatesMutation.mutate();
  };

  // Send all certificates to mail mutation
  const sendCertificatesToMailMutation = useMutation({
    mutationFn: async () => {
      return await ApiService.sendCertificatesToMail(eventId);
    },
    onSuccess: () => {
      setShowSendConfirmDialog(false);
      showSuccess('Certificates are being sent to participants via email. This may take a few minutes.');
    },
    onError: (error) => {
      setShowSendConfirmDialog(false);
      showError('Failed to send certificates. Please try again.');
      console.error('Send certificates error:', error);
    }
  });

  const handleSendToMail = () => {
    setShowSendConfirmDialog(true);
  };

  const confirmSendToMail = () => {
    sendCertificatesToMailMutation.mutate();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Certificates</h3>
          <p className="text-sm text-gray-600">Generate and manage certificates for event participants</p>
        </div>
      </div>

      <CertificateListPanel eventId={eventId} eventName={eventName} />

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Generate Certificates Card */}
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Plus className="w-5 h-5 text-blue-600" />
              <span>Generate Certificates</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Upload a CSV file with participant data and generate certificates for all participants.
            </p>
            <Button 
              onClick={() => setShowGeneratorModal(true)}
              className="w-full"
            >
              <FileText className="w-4 h-4 mr-2" />
              Generate Certificates
            </Button>
          </CardContent>
        </Card>

        {/* Download All Certificates Card */}
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Download className="w-5 h-5 text-green-600" />
              <span>Download All Certificates</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Download all generated certificates as a ZIP file for easy distribution.
            </p>
            
            {/* Progress Indicator */}
            {downloadProgress && (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700 font-medium">
                    {downloadProgress.status === 'downloading' ? 'Downloading certificates...' : 
                     downloadProgress.status === 'creating_zip' ? 'Creating ZIP file...' : 
                     'Starting...'}
                  </span>
                  <span className="text-gray-600">
                    {downloadProgress.current}/{downloadProgress.total} ({downloadProgress.percentage}%)
                  </span>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${downloadProgress.percentage}%` }}
                  />
                </div>
                
                {/* Progress Stats */}
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-3 h-3 text-green-600" />
                    <span>{downloadProgress.successful} successful</span>
                  </div>
                  {downloadProgress.failed > 0 && (
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="w-3 h-3 text-red-600" />
                      <span>{downloadProgress.failed} failed</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <Button 
              variant="outline"
              className="w-full"
              onClick={handleDownloadAll}
              disabled={downloadAllCertificatesMutation.isPending}
            >
              {downloadAllCertificatesMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  <span>Downloading...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  <span>Download All Certificates</span>
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Send Certificates Card */}
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Mail className="w-5 h-5 text-purple-600" />
              <span>Send All Certificates</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Send all generated certificates to participants via email. Each participant will receive their certificate at their registered email address.
            </p>
            
            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> This is an asynchronous operation. Emails will be sent in the background, which may take a few minutes depending on the number of participants.
              </p>
            </div>
            
            <Button 
              variant="outline"
              className="w-full"
              onClick={handleSendToMail}
              disabled={sendCertificatesToMailMutation.isPending}
            >
              {sendCertificatesToMailMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  <span>Send All Certificates</span>
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>How to Generate Certificates</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Step 1: Prepare Your CSV File</h4>
              <p className="text-sm text-blue-800">
                Create a CSV file with participant data. The file should include columns like:
                <br />• Name, Email, Registration ID, and any other relevant information
              </p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-medium text-green-900 mb-2">Step 2: Optional Certificate Template</h4>
              <p className="text-sm text-green-800">
                You can provide a custom certificate template URL. If not provided, a default template will be used.
              </p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg">
              <h4 className="font-medium text-purple-900 mb-2">Step 3: Generate and Preview</h4>
              <p className="text-sm text-purple-800">
                Use the "Show Certificate Preview" button to see how the certificate will look before generating all certificates.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Certificate Generator Modal */}
      <CertificateGeneratorModal
        eventId={eventId}
        isOpen={showGeneratorModal}
        onClose={() => setShowGeneratorModal(false)}
      />

      {/* Send Certificates Confirmation Dialog */}
      <Dialog open={showSendConfirmDialog} onOpenChange={setShowSendConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <span>Confirm Send Certificates</span>
            </DialogTitle>
            <DialogDescription className="pt-4">
              <div className="space-y-3">
                <p className="text-sm text-gray-700">
                  Are you sure you want to send certificates to all participants via email?
                </p>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">
                    <strong>Important:</strong> This action will trigger email delivery to all participants. 
                    The process runs in the background and may take several minutes to complete.
                  </p>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> Make sure you have configured email settings for this event 
                    and that certificates have been generated before sending.
                  </p>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => setShowSendConfirmDialog(false)}
              disabled={sendCertificatesToMailMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmSendToMail}
              disabled={sendCertificatesToMailMutation.isPending}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {sendCertificatesToMailMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  <span>Yes, Send All</span>
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CertificatesTab;
